<template>
  <div class="" style="position: relative;" data-testid="select-tag-textbox">
    <v-text-field
      ref="textfield"
      :value="value"
      outlined
      dense
      :label="label"
      :hide-details="hidedetails"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="onTextInput"
      @keydown="onKeyDown"
      class="my-0"
      data-testid="select-tag-textbox__input"
    />
    <v-menu
      v-model="showMenu"
      :close-on-content-click="false"
      offset-y
      transition="fade-transition"
      max-width="200"
      min-width="150"
      absolute
      :position-x="menuPosition.x"
      :position-y="menuPosition.y"
      data-testid="select-tag-textbox__menu"
    >
      <v-list dense data-testid="select-tag-textbox__menu-list">
        <v-list-item
          v-for="(item, index) in filteredTags"
          :key="item"
          :class="{ 'tag-item-highlighted': index === highlightedIndex }"
          @click="selectTag(item)"
          :data-testid="`select-tag-textbox__menu-item-${index}`"
        >
          <v-list-item-title :data-testid="`select-tag-textbox__menu-item-text-${index}`">{{ item }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'SelectTagTextBox',
  props: {
    value: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: "Input"
    },
    placeholder: {
      type: String,
      default: ""
    },
    hidedetails: {
      type: Boolean,
      default: true
    },
    allTagsList: {
      type: Array,
      default: () => ["#AI"]
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showMenu: false,
      filteredTags: [],
      highlightedIndex: 0,
      menuPosition: { x: 0, y: 0 },
      triggerIndex: -1,
      selectedTags: [],
      menuOffsetY: 5, // Vertical offset for menu positioning
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        // Extract tags from the text (words starting with #)
        if (newValue) {
          const tagMatches = newValue.match(/#\w+/g);
          this.selectedTags = tagMatches ? [...new Set(tagMatches)] : [];
        } else {
          this.selectedTags = [];
        }
      }
    },
    showMenu(newVal) {
      // Reset highlighted index when menu opens
      if (newVal) {
        this.highlightedIndex = 0;
      }
    }
  },
  methods: {
    getCursorCoordinates(inputEl, position) {
      try {
        // Create a mirror div to calculate cursor position
        const div = document.createElement('div');
        const style = window.getComputedStyle(inputEl);
        
        // Copy styles from input to mirror div
        ['fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight',
         'padding', 'border', 'boxSizing', 'width'].forEach(prop => {
          div.style[prop] = style[prop];
        });
        
        div.style.position = 'absolute';
        div.style.visibility = 'hidden';
        div.style.whiteSpace = 'pre';
        
        // Insert text up to cursor position
        const textBeforeCursor = inputEl.value.substring(0, position);
        div.textContent = textBeforeCursor;
        
        // Add a span to mark cursor position
        const span = document.createElement('span');
        span.textContent = '.';
        div.appendChild(span);
        
        document.body.appendChild(div);
        
        const coordinates = {
          top: span.offsetTop,
          left: span.offsetLeft,
          height: span.offsetHeight
        };
        
        document.body.removeChild(div);
        return coordinates;
      } catch (error) {
        console.error('Error calculating cursor coordinates:', error);
        return null;
      }
    },

    onTextInput(value) {
      // Emit the input event to update parent's v-model
      this.$emit('input', value);
      
      // Get text field element reference
      const inputEl = this.$refs.textfield.$refs.input;
      const cursorPos = inputEl.selectionStart;
      const hashIndex = value.lastIndexOf("#", cursorPos - 1);
        
      if (hashIndex !== -1) {
        const afterHash = value.slice(hashIndex + 1, cursorPos);
        
        // Check if there's a space between # and cursor (means tag is complete)
        if (afterHash.includes(" ")) {
          this.showMenu = false;
          return;
        }
        
        // Filter tags: remove # from tag when comparing
        this.filteredTags = this.allTagsList.filter(tag => {
          const tagWithoutHash = tag.startsWith('#') ? tag.slice(1) : tag;
          return tagWithoutHash.toLowerCase().includes(afterHash.toLowerCase());
        });
        this.triggerIndex = hashIndex;
        this.highlightedIndex = 0; // Reset to first item

        if (this.filteredTags.length > 0) {
          this.showMenu = true;
          const rect = inputEl.getBoundingClientRect();
          
          try {
            const coords = this.getCursorCoordinates(inputEl, cursorPos);
            
            if (coords) {
              // Position menu at cursor location
              this.menuPosition = { 
                x: rect.left + coords.left, 
                y: rect.bottom + this.menuOffsetY 
              };
            } else {
              // Fallback positioning below the input
              this.menuPosition = { x: rect.left, y: rect.bottom + this.menuOffsetY };
            }
          } catch (error) {
            console.error('Error positioning menu:', error);
            // Fallback positioning below the input
            this.menuPosition = { x: rect.left, y: rect.bottom + this.menuOffsetY };
          }
        } else {
          this.showMenu = false;
        }
      } else {
        this.showMenu = false;
      }
    },

    onKeyDown(event) {
      if (!this.showMenu || this.filteredTags.length === 0) {
        return;
      }

      // Handle arrow down key
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredTags.length;
      }
      // Handle arrow up key
      else if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        this.highlightedIndex = this.highlightedIndex === 0 
          ? this.filteredTags.length - 1 
          : this.highlightedIndex - 1;
      }
      // Handle Enter key
      else if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        const selectedTag = this.filteredTags[this.highlightedIndex];
        if (selectedTag) {
          this.selectTag(selectedTag);
          // Focus back to text field and set cursor position
          this.$nextTick(() => {
            const inputEl = this.$refs.textfield.$refs.input;
            if (inputEl) {
              inputEl.focus();
              // Set cursor position after the inserted tag
              const newCursorPos = this.triggerIndex + selectedTag.length + 1;
              inputEl.setSelectionRange(newCursorPos, newCursorPos);
            }
          });
        }
      }
      // Handle Escape key to close menu
      else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.showMenu = false;
      }
    },

    selectTag(tag) {
      if (this.triggerIndex !== -1) {
        const before = this.value.slice(0, this.triggerIndex);
        const after = this.value.slice(
          this.value.indexOf(" ", this.triggerIndex) !== -1
            ? this.value.indexOf(" ", this.triggerIndex)
            : this.value.length
        );
        
        const newValue = `${before}${tag} ${after}`;
        // Emit the updated value to parent
        this.$emit('input', newValue);
        this.showMenu = false;
      }
    },
  },
};
</script>

<style scoped>
.tag-item-highlighted {
  background-color: var(--color-pending-approval-bg) !important;
  color: var(--color-pending-approval) !important;
}

.tag-item-highlighted .v-list-item-title {
  color: var(--color-pending-approval) !important;
  font-weight: 500;
}
</style>